import React, { Fragment } from 'react';
import Link from 'components/general/Link/Link';
import Button from 'components/general/Button/Button';
import Icon from 'components/general/Icon/Icon';
import SplitButton from 'components/general/SplitButton/SplitButton';
import LinkProps from 'utils/ts/LinkProps';
import ActionMenu from 'components/general/ActionMenu/ActionMenu';
import Spinner from 'components/general/Spinner/Spinner';
import Tooltip from 'components/general/Tooltip/Tooltip';
import withPrompt from 'components/hoc/withPrompt';

const renderActions = (actions: ActionDef[]) =>
  actions.map((action) =>
    action.renderPrompt ? (
      <ActionPrompt {...action} key={action.title} />
    ) : (
      <ActionButton {...action} key={action.title} />
    ),
  );

const ActionPrompt = withPrompt()(({ openPrompt, ...args }) => (
  <ActionButton {...args} onClick={openPrompt} />
));

const ActionButton = ({
  to,
  event,
  types,
  icon,
  title,
  className,
  preTitle,
  onClick,
  pending,
  disabled,
  tooltip,
  tooltipPlacement,
}) => {
  const button = (
    <Button
      to={to}
      onClick={onClick}
      types={['sm'].concat(types || [])}
      className={className}
      event={event}
      pending={pending}
      disabled={disabled || pending || undefined}
    >
      {pending
          ? <Spinner types={['xs', 'push-right']} className="spinner--dark" />
          : icon && <Icon name={icon} types={['push-right']} />
      }
      <span>
        {preTitle && <span className="btn__pre-title">{preTitle}</span>}
        {title}
      </span>
    </Button>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement={tooltipPlacement || 'bottom'}>
      {button}
    </Tooltip>
  ) : button;
};

const PageHeader: React.SFC<PropDef> = ({
  title,
  flag,
  section,
  actions,
  navigation,
}) => (
  <header className="page-header">
    <div className="page-header__inner">
      <div className="page-header__main">
        {section && (
          <Fragment>
            {section.to ? (
              <Link
                to={section.to}
                className="page-header__subtitle"
                data-test="page-title"
              >
                {section.title}
              </Link>
            ) : (
              <span className="page-header__subtitle">{section.title}</span>
            )}
          </Fragment>
        )}
        <h1 className="page-header__title" data-test="page-title">
          {title}
          {flag && <span className={`flag-icon flag-icon--${flag.toLowerCase()}`} />}
        </h1>
        {navigation && (
          <div className="page-header__navigation">
            <ActionMenu
              category={navigation.category}
              title={navigation.title}
              actions={navigation.items}
              btnProps={{ types: ['sm', 'slim'] }}
            />
          </div>
        )}
      </div>
      {actions && (
        <div className="page-header__actions">
          {actions.length &&
            actions.map((action) => (
              <div className="page-header__action" key={action.title}>
                {Array.isArray(action) ? (
                  <SplitButton>{renderActions(action)}</SplitButton>
                ) : (
                  renderActions([action])
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  </header>
);

PageHeader.defaultProps = {
  actions: null,
};

export interface ActionDef {
  title: string;
  icon: string;
  types?: string[];
  to?: LinkProps;
  onClick?();
  renderPrompt?();
  className?: string;
  disabled?: boolean;
  pending?: boolean;
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom';
}

interface PropDef {
  title: string;
  flag?: string;
  section?: {
    title: string;
    to?: LinkProps;
  };
  actions?: ActionDef[];
}

export default PageHeader;
